import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Skeleton,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Add as AddIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import SupplierModal from "./modals/SupplierModal";
import CustomConfirmationDialog from "../dialogs/CustomConfirmationDialog";
import DeliveryCenterCostsService from "../../api/DeliveryCenter/deliveryCenterCosts";

const ACCENT = "#059669";
const ACCENT_DARK = "#047857";

const listItemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.3 },
  }),
  removed: {
    opacity: 0,
    x: -100,
    transition: { duration: 0.2 },
  },
};

const SupplierManagement = () => {
  const theme = useTheme();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const response = await DeliveryCenterCostsService.getSuppliers();
      const suppliersData = response.data.results || response.data;
      setSuppliers(suppliersData);
    } catch (error) {
      console.error("Erro ao buscar fornecedores:", error);
      showAlert(
        "Falha ao carregar fornecedores. Por favor, tente novamente.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddSupplier = () => {
    setCurrentItem(null);
    setEditMode(false);
    setModalOpen(true);
  };

  const handleEditSupplier = (supplier) => {
    setCurrentItem(supplier);
    setEditMode(true);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentItem(null);
  };

  const handleDeleteSupplier = async (supplierId) => {
    try {
      await DeliveryCenterCostsService.deleteSupplier(supplierId);
      showAlert("Fornecedor excluido com sucesso!", "success");
      fetchSuppliers();
    } catch (error) {
      console.error("Erro ao excluir fornecedor:", error);
      showAlert(
        "Falha ao excluir fornecedor. Por favor, tente novamente.",
        "error"
      );
    }
  };

  const handleModalSuccess = (message) => {
    showAlert(message, "success");
    fetchSuppliers();
    handleCloseModal();
  };

  const showAlert = (message, severity = "success") => {
    setAlertInfo({ open: true, message, severity });
    setTimeout(() => {
      setAlertInfo((prev) => ({ ...prev, open: false }));
    }, 5000);
  };

  const filteredItems = suppliers.filter((item) =>
    (item.name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (itemToDelete) {
      handleDeleteSupplier(itemToDelete.id);
    }
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Alert */}
      <Collapse in={alertInfo.open}>
        <Alert
          severity={alertInfo.severity}
          onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
          sx={{
            mb: 3,
            borderRadius: 2,
            ...(alertInfo.severity === "success" && {
              backgroundColor: alpha(ACCENT, 0.08),
              color: ACCENT_DARK,
              "& .MuiAlert-icon": { color: ACCENT },
            }),
          }}
        >
          {alertInfo.message}
        </Alert>
      </Collapse>

      {/* ─── SUPPLIERS LIST ─── */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* List header */}
          <Box
            sx={{
              p: 2.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: `1px solid ${theme.palette.divider}`,
              background: alpha(ACCENT, 0.03),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, fontSize: "0.9rem" }}
              >
                Fornecedores
              </Typography>
              <Chip
                label={loading ? "..." : suppliers.length}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  backgroundColor: alpha(ACCENT, 0.1),
                  color: ACCENT,
                }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip title="Atualizar lista">
                <IconButton
                  onClick={fetchSuppliers}
                  disabled={loading}
                  size="small"
                  sx={{
                    color: ACCENT,
                    "&:hover": { backgroundColor: alpha(ACCENT, 0.08) },
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSupplier}
                disabled={loading}
                size="small"
                sx={{
                  ml: 0.5,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${ACCENT_DARK} 0%, ${ACCENT} 100%)`,
                  boxShadow: `0 2px 8px ${alpha(ACCENT, 0.3)}`,
                  "&:hover": {
                    boxShadow: `0 4px 14px ${alpha(ACCENT, 0.4)}`,
                  },
                }}
              >
                Novo Fornecedor
              </Button>
            </Box>
          </Box>

          {/* Search bar */}
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <TextField
              fullWidth
              placeholder="Buscar fornecedores..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{
                        fontSize: 18,
                        color: theme.palette.text.disabled,
                      }}
                    />
                  </InputAdornment>
                ),
                ...(searchTerm && {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm("")}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontSize: "0.82rem",
                  "&:hover fieldset": { borderColor: alpha(ACCENT, 0.3) },
                  "&.Mui-focused fieldset": { borderColor: ACCENT },
                },
              }}
            />
          </Box>

          {/* List content */}
          <Box sx={{ maxHeight: 440, overflowY: "auto" }}>
            {loading ? (
              <Box sx={{ p: 2.5 }}>
                {[...Array(3)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      py: 1.5,
                    }}
                  >
                    <Skeleton
                      variant="circular"
                      width={36}
                      height={36}
                      sx={{ flexShrink: 0 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="50%" height={20} />
                      <Skeleton width="35%" height={16} />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : filteredItems.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 6,
                  px: 3,
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    background: alpha(ACCENT, 0.06),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  <BusinessIcon
                    sx={{ fontSize: 28, color: alpha(ACCENT, 0.35) }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    mb: 0.5,
                  }}
                >
                  Nenhum fornecedor encontrado
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.disabled,
                    textAlign: "center",
                    maxWidth: 280,
                    mb: 2.5,
                  }}
                >
                  Cadastre o primeiro fornecedor para organizar seus parceiros
                  comerciais.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddSupplier}
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${ACCENT_DARK} 0%, ${ACCENT} 100%)`,
                    boxShadow: `0 2px 8px ${alpha(ACCENT, 0.3)}`,
                    "&:hover": {
                      boxShadow: `0 4px 14px ${alpha(ACCENT, 0.4)}`,
                    },
                  }}
                >
                  Novo Fornecedor
                </Button>
              </Box>
            ) : (
              <List disablePadding>
                <AnimatePresence>
                  {filteredItems
                    .slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                    .map((item, index) => (
                      <React.Fragment key={item.id}>
                        <motion.div
                          initial="hidden"
                          animate="visible"
                          exit="removed"
                          variants={listItemVariants}
                          custom={index}
                          layoutId={`supplier-item-${item.id}`}
                        >
                          <ListItem
                            sx={{
                              px: 2.5,
                              py: 1.5,
                              transition: "background 0.15s ease",
                              "&:hover": {
                                backgroundColor: alpha(ACCENT, 0.03),
                              },
                            }}
                          >
                            {/* Supplier icon */}
                            <Box
                              sx={{
                                mr: 2,
                                width: 36,
                                height: 36,
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                background: alpha(ACCENT, 0.08),
                              }}
                            >
                              <BusinessIcon
                                sx={{ fontSize: 18, color: ACCENT }}
                              />
                            </Box>

                            <ListItemText
                              primary={
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    {item.name}
                                  </Typography>
                                  {item.contact_email && (
                                    <Chip
                                      size="small"
                                      label={item.contact_email}
                                      sx={{
                                        height: 20,
                                        fontSize: "0.6rem",
                                        fontWeight: 600,
                                        backgroundColor: alpha(ACCENT, 0.1),
                                        color: ACCENT,
                                        border: `1px solid ${alpha(ACCENT, 0.2)}`,
                                      }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                item.contact_name ? (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: "0.72rem",
                                      color: theme.palette.text.disabled,
                                    }}
                                  >
                                    {item.contact_name}
                                  </Typography>
                                ) : null
                              }
                            />

                            <ListItemSecondaryAction>
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditSupplier(item)}
                                  sx={{
                                    mr: 0.5,
                                    color: ACCENT,
                                    "&:hover": {
                                      backgroundColor: alpha(ACCENT, 0.08),
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Excluir">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(item)}
                                  sx={{
                                    color: theme.palette.error.main,
                                    "&:hover": {
                                      backgroundColor: alpha(
                                        theme.palette.error.main,
                                        0.08
                                      ),
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </ListItemSecondaryAction>
                          </ListItem>
                        </motion.div>
                        {index <
                          filteredItems.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          ).length -
                            1 && <Divider component="li" sx={{ mx: 2.5 }} />}
                      </React.Fragment>
                    ))}
                </AnimatePresence>
              </List>
            )}
          </Box>

          {/* Pagination */}
          {filteredItems.length > 0 && (
            <TablePagination
              component="div"
              count={filteredItems.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Itens por pagina:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            />
          )}
        </Paper>
      </motion.div>

      {/* Confirmation Dialog */}
      <CustomConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusao"
        dialogContent={`Tem certeza que deseja excluir "${itemToDelete?.name}"?`}
        confirmText="Excluir"
        cancelText="Cancelar"
      />

      {/* Supplier Modal */}
      <SupplierModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        editMode={editMode}
        initialData={currentItem}
        onSuccess={handleModalSuccess}
      />
    </Box>
  );
};

export default SupplierManagement;
